import React, { useEffect, useState } from "react";
import AnalyticsProfileForm from "./AnalyticsProfileForm";
import { useMsal } from "@azure/msal-react";
import { AddAnalyticsProfile, UpdateAnalyticsProfile, GetAnalyticsProfile } from "../../../Services/HeedifyServices";
import { getAccessToken } from "../../../Services/GraphServices";
import { Dismiss20Regular, Save20Regular, Edit20Regular } from "@fluentui/react-icons";
import { useNavigate, useParams } from "react-router-dom";
import { useNotification } from "../../GenericComponent/NotificationSystem";
import { useTranslation } from "react-i18next";
import {
  Button,
  Spinner,
} from "@fluentui/react-components";

const AnalyticsProfileComponent = (props) => {
  const { instance, accounts } = useMsal();
  const navigate = useNavigate();
  const activeAccount = accounts?.[0];
  const { showSuccess, showError } = useNotification();
  const { t } = useTranslation();
  const tenantId = props.ACTIVE_TENANT || activeAccount?.tenantId || null;

  const [saving, setSaving] = useState(false);
  const { profileId } = useParams();
  const isEditMode = profileId && profileId !== "newProfile";

  const [profile, setProfile] = useState({
    scope: "",
    reportConfig: {
      thresholds: {
        quickDropThreshold: 6,
        // Tranches de rapidité de décroché (widget « Répartition des réponses ») :
        // rapide < 30 s · standard 30-60 s · lent > 60 s.
        quickAnswerThreshold: 30,
        standardAnswerThreshold: 60,
        delayedAnswerThreshold: 90,
        // Seuil SLA, en SECONDES : un appel décroché dans ce délai est « dans le SLA ».
        // Il n'existait pas — le SLA était tiré de waitingTime.targetWaitingTime (150 s),
        // qui est la cible de colorisation du widget « Max Waiting Time », pas un SLA.
        slaSeconds: 60,
      },
      abandonTime: {
        maxAbandonTime: 180,
        targetAbandonTime: 90,
      },
      talkingTime: {
        maxTalkingTime: 600,
        targetTalkingTime: 370,
      },
      waitingTime: {
        maxWaitingTime: 360,
        targetWaitingTime: 150,
      },
    },
    dashboardLayout: {
      tabs: {
        dashboard: {
          displayName: "Dashboard",
          type: "system",
          widgets: {},
          widgetsPositions: {},
          createdAt: new Date().toISOString()
        },
        calls: {
          displayName: "Calls List",
          type: "system",
          widgets: {},
          widgetsPositions: {},
          createdAt: new Date().toISOString()
        }
      }
    }
  });

  const [formValid, setFormValid] = useState(false);
  const [loading, setLoading] = useState(true);

  // Fetch profile if in edit mode
  useEffect(() => {
    if (isEditMode) {
      getAccessToken(instance, activeAccount)
        .then((token) => GetAnalyticsProfile(tenantId, profileId, token))
        .then((result) => {
          if (result.success && result.data) {
            setProfile(result.data);
          }
        })
        .catch((err) => console.error("❌ Error fetching analytics profile:", err))
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  }, [profileId, instance, activeAccount, tenantId, isEditMode]);

  const handleSave = (data) => {
    setSaving(true);
    getAccessToken(instance, activeAccount)
      .then((token) => {
        if (isEditMode) {
          return UpdateAnalyticsProfile(tenantId, profileId, data, token);
        } else {
          return AddAnalyticsProfile(tenantId, data, token);
        }
      })
      .then((result) => {
        if (result?.success && result?.data) {
          const newProfile = result.data;

          // Update Redux store
          const updatedProfiles = {
            ...props.ANALYTICS_PROFILES,
            [newProfile.id]: newProfile,
          };
          props.setAnalyticsProfiles(updatedProfiles);
          
          if (isEditMode) {
            showSuccess(t("ANALYTICS_PROFILE_UPDATED_SUCCESS"));
          } else {
            showSuccess(t("ANALYTICS_PROFILE_CREATED_SUCCESS"));
          }
          navigate("/analyticsProfile");
        } else {
          if (isEditMode) {
            showError(t("ANALYTICS_PROFILE_UPDATE_FAILED"));
          } else {
            showError(t("ANALYTICS_PROFILE_CREATION_FAILED"));
          }
        }
      })
      .catch((error) => {
        console.error("❌ Error while saving analytics profile:", error);
        if (isEditMode) {
          showError(t("ANALYTICS_PROFILE_UPDATE_ERROR"));
        } else {
          showError(t("ANALYTICS_PROFILE_CREATION_ERROR"));
        }
      })
      .finally(() => {
        setSaving(false);
      });
  };

  if (loading) return <div>Loading...</div>;

  return (
    <>
      <div style={{ display: 'flex', gap: '10px', marginBottom: '20px', justifyContent: 'flex-end' }}>
        <Button
          icon={<Dismiss20Regular />}
          appearance="secondary"
          onClick={() => navigate("/analyticsProfile")}
        >
          {t("CANCEL")}
        </Button>
        <Button
          icon={saving ? <Spinner size="tiny" /> : (isEditMode ? <Edit20Regular /> : <Save20Regular />)}
          disabled={!formValid || saving}
          appearance="primary"
          onClick={() => handleSave(profile)}
        >
          {saving ? t("SAVING") : t("SAVE")}
        </Button>
      </div>
      <div style={{  display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
        <AnalyticsProfileForm
          profile={profile}
          setProfile={setProfile}
          formValid={formValid}
          setFormValid={setFormValid}
          onSave={handleSave}
          isEditMode={isEditMode}
          SCOPES_NAMES={props.SCOPES_NAMES}
        />
      </div>
    </>
  );
};

export default AnalyticsProfileComponent;
